import React from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { notAvailableYetHint } from '@/lib/notAvailableYet';
import {
  MERCHANT_STATUSES,
  MERCHANT_STATUS_GROUPS,
  MerchantStatusDefinition,
  MerchantStatusGroup,
  MerchantStatusId,
  getStatusFilterLabel,
} from '@/shared/merchantStatus';
import { MerchantStatusBadge } from './MerchantStatusBadge';

// Status filter pill (REQ-011): group checkboxes for Pipeline and Not in
// pipeline select or clear every status in the group; each status has its own
// checkbox and count. Statuses the backend cannot store yet are disabled.

interface MerchantStatusFilterProps {
  selected: ReadonlySet<MerchantStatusId>;
  counts: Readonly<Partial<Record<MerchantStatusId, number>>>;
  onChange: (ids: Iterable<MerchantStatusId>) => void;
}

const STATUS_BACKEND_USE_CASE = 'UC-214';

const membersOf = (group: MerchantStatusGroup): MerchantStatusDefinition[] =>
  MERCHANT_STATUSES.filter((s) => s.group === group);

export const MerchantStatusFilter: React.FC<MerchantStatusFilterProps> = ({ selected, counts, onChange }) => {
  const toggleStatus = (id: MerchantStatusId) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleGroup = (members: MerchantStatusDefinition[], allChecked: boolean) => {
    const next = new Set(selected);
    members.forEach((s) => (allChecked ? next.delete(s.id) : next.add(s.id)));
    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 gap-2 whitespace-nowrap px-3">
          <ListFilter className="h-4 w-4" aria-hidden="true" />
          Status: {getStatusFilterLabel(selected)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-2">
        <div role="group" aria-label="Filter by status">
          {MERCHANT_STATUS_GROUPS.map((group) => {
            const members = membersOf(group);
            const onCount = members.filter((s) => selected.has(s.id)).length;
            const allChecked = onCount === members.length;
            const isMaster = group !== 'Other';
            const groupAvailable = members.some((s) => s.available);
            const groupId = `status-group-${group.replace(/\s+/g, '-').toLowerCase()}`;

            return (
              <div key={group} className="mt-1 first:mt-0">
                {isMaster ? (
                  <label
                    htmlFor={groupId}
                    title={groupAvailable ? undefined : notAvailableYetHint(STATUS_BACKEND_USE_CASE)}
                    className="flex min-h-9 items-center gap-2.5 rounded px-2 hover:bg-muted/60"
                  >
                    <Checkbox
                      id={groupId}
                      aria-label={group}
                      checked={allChecked ? true : onCount > 0 ? 'indeterminate' : false}
                      disabled={!groupAvailable}
                      onCheckedChange={() => toggleGroup(members, allChecked)}
                    />
                    <span className="text-sm font-semibold">{group}</span>
                    {onCount > 0 && !allChecked && (
                      <span className="ml-auto text-xs text-muted-foreground">{onCount} of {members.length}</span>
                    )}
                  </label>
                ) : (
                  <div className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">{group}</div>
                )}
                {members.map((status) => {
                  const optionId = `status-option-${status.id}`;
                  return (
                    <label
                      key={status.id}
                      htmlFor={optionId}
                      title={status.available ? undefined : notAvailableYetHint(STATUS_BACKEND_USE_CASE)}
                      className={`flex min-h-9 items-center gap-2.5 rounded pr-2 hover:bg-muted/60 ${isMaster ? 'pl-8' : 'pl-2'}`}
                    >
                      <Checkbox
                        id={optionId}
                        aria-label={status.label}
                        checked={selected.has(status.id)}
                        disabled={!status.available}
                        onCheckedChange={() => toggleStatus(status.id)}
                      />
                      <MerchantStatusBadge status={status.id} className={status.available ? undefined : 'opacity-60'} />
                      <span className="ml-auto text-xs text-muted-foreground">
                        {status.available ? counts[status.id] ?? 0 : 'Not available yet'}
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          })}
          <p className="mt-2 border-t px-2 pt-2 text-xs text-muted-foreground">
            Your selection is remembered and applied next time you open this page.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MerchantStatusFilter;
